import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import BelowFoldViewHandler from '@/vdp/Analytics/BelowFoldViewHandler';
import Breadcrumbs from '@/vdp/BreadCrumbs';
import dynamicLoader from '@/vdp/dynamicLoader';
import { MobileGallery } from '@/vdp/Gallery';
import Hero from '@/vdp/Hero';
import { scrollTopClasses } from '@/vdp/styles';
import ShowConditionally from '@/vdp/VehicleDetailsContainer/ShowConditionally';
import StickyBannerObserver from '@/vdp/VehicleDetailsContainer/StickyBannerObserver';
import VehicleInfoCard from '@/vdp/VehicleInfoCard';
import { isTest } from '~/constants/environment';
import Section, { SectionId } from '~/constants/vdp/section';
import { useIsAppRouter } from '~/context/common/StaticContext';
import { NavigationStickyContextProvider } from '~/context/vdp/NavigationStickyContext';
import { useIsNewVehicle } from '~/context/vdp/VehicleContext';
import { useShowPriceDetailsImmediately } from '~/hooks/vdp';
import useShouldLoadBelowFirstSectionStandard from '~/hooks/vdp/selectors/useShouldLoadBelowFirstSectionStandard';
import useShouldLoadFirstSection from '~/hooks/vdp/selectors/useShouldLoadFirstSection';
import useShowAboutPrice from '~/hooks/vdp/selectors/useShowAboutPrice';
import useIsInInspectionExperiment from '~/hooks/vdp/useIsInInspectionExperiment';
import useIsInMiniHeroExperiment from '~/hooks/vdp/useIsInMiniHeroExperiment';
import useShowPageNavigation from '~/hooks/vdp/useShowPageNavigation';
import NativeError from '~/native/NativeError';
import LowerSections from './LowerSections';
import {
  GridLayout,
  MainContentWrapper,
  RecommendedVehiclesSectionWrapper,
  sectionBottomPadding,
  sectionTopPadding,
  sectionYPadding
} from './styles';
import { ContentWrapper, Wrapper } from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice'),
  { ssr: false }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  { ssr: false, loading: dynamicLoader(Section.Disclaimer) }
);

const DesktopPageNavigation = dynamic(
  () =>
    import(
      /* webpackChunkName: "DesktopPageNavigation" */ '@/vdp/DesktopPageNavigation'
    )
);

const FeatureBranch = dynamic(
  () =>
    import(/* webpackChunkName: "FeatureBranch" */ '@/common/FeatureBranch'),
  { ssr: false }
);

const FeaturesAndHighlights = dynamic(
  () =>
    import(
      /* webpackChunkName: "FeaturesAndHighlights" */ '@/vdp/FeaturesAndHighlights'
    ),
  { ssr: false, loading: dynamicLoader(Section.FeaturesAndHighlights) }
);

const GeicoBanner = dynamic(
  () => import(/* webpackChunkName: "GeicoBanner" */ '@/vdp/GeicoBanner'),
  { ssr: false }
);

const RootBanner = dynamic(
  () => import(/* webpackChunkName: "RootBanner" */ '@/vdp/RootBanner'),
  { ssr: false }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const MobileCta = dynamic(
  () => import(/* webpackChunkName: "MobileCta" */ '@/vdp/MobileCta'),
  { ssr: false }
);

const NeedItSooner = dynamic(
  () => import(/* webpackChunkName: "NeedItSooner" */ '@/vdp/NeedItSooner'),
  {
    ssr: false
  }
);

const NewCarBanner = dynamic(
  () => import(/* webpackChunkName: "NewCarBanner" */ '@/vdp/NewCarBanner'),
  {
    ssr: false
  }
);

const PickupAndDelivery = dynamic(
  () =>
    import(
      /* webpackChunkName: "PickupAndDelivery" */ '@/vdp/PickupAndDelivery'
    ),
  { ssr: false, loading: dynamicLoader(Section.PickupAndDelivery) }
);

const PriceDetails = dynamic(
  () => import(/* webpackChunkName: "PriceDetails" */ '@/vdp/PriceDetails'),
  { ssr: false, loading: dynamicLoader(Section.PriceDetails) }
);

const QualiSurvey = dynamic(
  () => import(/* webpackChunkName: "QualiSurvey" */ '@/vdp/QualiSurvey'),
  { ssr: false }
);

const RecommendedVehicles = dynamic(
  () =>
    import(
      /* webpackChunkName: "RecommendedVehicles" */ '@/vdp/RecommendedVehicles'
    ),
  { ssr: false, loading: dynamicLoader(Section.RecommendedVehicles) }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const SubscribedToNotificationsBanner = dynamic(
  () =>
    import(
      /* webpackChunkName: "SubscribedToNotificationsBanner" */ '@/vdp/SubscribedToNotificationsBanner'
    ),
  { ssr: false }
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  { ssr: false, loading: dynamicLoader(Section.Disclaimer) }
);

const WhyBuy = dynamic(
  () => import(/* webpackChunkName: "WhyBuy" */ '@/vdp/WhyBuy')
);

export default function VehicleDetailsStandardContainer() {
  const isAppRouter = useIsAppRouter();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isNewVehicle = useIsNewVehicle();
  const isInInspectionExperiment = useIsInInspectionExperiment();
  const isInMiniHeroExperiment = useIsInMiniHeroExperiment();

  const shouldLoadFirstSection = useShouldLoadFirstSection();
  const shouldLoadBelowFirstSection = useShouldLoadBelowFirstSectionStandard();
  const showPriceDetailsImmediately = useShowPriceDetailsImmediately();

  const { showAboutPrice } = useShowAboutPrice();
  const showPageNavigation = useShowPageNavigation();

  return (
    <NavigationStickyContextProvider>
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-canvas-default focus:p-md focus:t-body-sm"
        data-adobe-id={null}
        data-analytics-id="Navigation - Skip to Main Content"
        data-testid="skip-to-main-content"
        href={`#${SectionId.Root}`}
      >
        Skip to main content
      </a>
      {!isAppRouter && <PageHeader />}
      {isTest && !isAppRouter && <FeatureBranch />}
      <NativeError />
      <Wrapper ref={wrapperRef} id={SectionId.Root}>
        <SubscribedToNotificationsBanner />
        <ContentWrapper>
          <Breadcrumbs />
          <GridLayout ref={gridRef}>
            <Hero />

            {showPageNavigation && <DesktopPageNavigation />}

            <VehicleInfoCard />

            <MainContentWrapper>
              <ShowConditionally wip={false} operation="and" mobile>
                <MobileGallery />
              </ShowConditionally>
              <ShowConditionally mobile>
                <StickyBannerObserver />
              </ShowConditionally>
              {isNewVehicle && <NewCarBanner />}
              <ShowConditionally notAvailableForImmediatePurchase>
                <RecommendedVehiclesSectionWrapper
                  isInMiniHeroExperiment={isInMiniHeroExperiment}
                >
                  <RecommendedVehicles isInGridLayout locationOnPage="top" />
                </RecommendedVehiclesSectionWrapper>
              </ShowConditionally>
              <div className="sm:pl-2xl md:pl-0">
                <WhyBuy />
                <ShowConditionally rootDiscount>
                  <RootBanner />
                </ShowConditionally>
                <ShowConditionally geicoDiscount>
                  <GeicoBanner />
                </ShowConditionally>
              </div>
              <ShowConditionally aboutPrice>
                <AboutThePrice gridRef={gridRef} wrapperRef={wrapperRef} />
              </ShowConditionally>
              <ShowConditionally deliveryPickup>
                <div
                  className={cx('flex flex-col gap-4xl', sectionBottomPadding, {
                    [sectionTopPadding]: showAboutPrice
                  })}
                >
                  <PickupAndDelivery />
                  <NeedItSooner />
                </div>
              </ShowConditionally>

              <FeaturesAndHighlights
                gridRef={gridRef}
                wrapperRef={wrapperRef}
              />

              <BelowFoldViewHandler
                id={SectionId.VehicleDetails}
                data-nav-section={SectionId.VehicleDetails}
                className={cx(SectionId.VehicleDetails, scrollTopClasses, {
                  'will-load': !shouldLoadFirstSection
                })}
              >
                <ShowConditionally renderFirstSection>
                  <VehicleDetails />
                </ShowConditionally>
              </BelowFoldViewHandler>

              <BelowFoldViewHandler
                id={SectionId.PriceDetails}
                data-nav-section={SectionId.PriceDetails}
                className={cx(SectionId.PriceDetails, scrollTopClasses, {
                  'will-load': !shouldLoadBelowFirstSection
                })}
              >
                {(shouldLoadBelowFirstSection ||
                  showPriceDetailsImmediately) && (
                  <PriceDetails gridRef={gridRef} wrapperRef={wrapperRef} />
                )}
              </BelowFoldViewHandler>

              <LowerSections
                gridRef={gridRef}
                shouldLoadBelowFirstSection={shouldLoadBelowFirstSection}
                wrapperRef={wrapperRef}
              />
            </MainContentWrapper>
          </GridLayout>
        </ContentWrapper>
        <ShowConditionally notAvailableForImmediatePurchase={false}>
          <BelowFoldViewHandler
            id={SectionId.RecommendedVehicles}
            data-nav-section={SectionId.RecommendedVehicles}
            className={cx(SectionId.RecommendedVehicles, {
              'will-load': !shouldLoadBelowFirstSection
            })}
          >
            <ShowConditionally renderBelowFirstSection>
              <div
                className={
                  isInInspectionExperiment
                    ? 'bg-canvas-default'
                    : 'bg-canvas-muted'
                }
              >
                <ContentWrapper className={cx('px-2xl', sectionYPadding)}>
                  <RecommendedVehicles
                    isInGridLayout={false}
                    locationOnPage="default"
                  />
                </ContentWrapper>
              </div>
            </ShowConditionally>
          </BelowFoldViewHandler>
        </ShowConditionally>
        <Disclaimer />
        <ShowConditionally mobile>
          <StickyBanner />
        </ShowConditionally>
        <ShowConditionally mobile>
          <MobileCta />
        </ShowConditionally>
      </Wrapper>

      {!isAppRouter && <PageFooter className="bg-canvas-muted" />}
      <Modals />
      <QualiSurvey />
    </NavigationStickyContextProvider>
  );
}
